import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AdminServerToClientEvents, FusionContent, FusionContentCaptivate, FusionContentHtmlPage, FusionContentIframely, FusionContentLxt, FusionContentMiroByTemplate, FusionContentMiroByUrl, FusionContentRise, FusionContentStoryline, FusionContentVideo, FusionContentYoutube, FusionFileFrontend, FusionRiseDefaults, FusionVideoDefaults, SpfBucketFileFrontend } from '@shared/index';
import { firstValueFrom, Subject } from 'rxjs';
import { CounterService } from './counter.service';
import { LanguageService } from './language.service';
import { LoginService } from './login.service';
import { BotidService } from './ragai/botid.service';
import { SocketAdminService } from './socketadmin.service';
import { ToastrService } from './toastr.service';

export type DefaultsResult = { rise: FusionRiseDefaults, video: FusionVideoDefaults }

@Injectable({
  providedIn: 'root'
})
export class FusionService {
  public fusionContent: SpfBucketFileFrontend<string, FusionContent<string>>[] = [];
  public selectedFusionContent?: SpfBucketFileFrontend<string, FusionContent<string>>;
  public selectedHtmlPage?: SpfBucketFileFrontend<string, FusionContentHtmlPage>;
  public selectedRise?: SpfBucketFileFrontend<string, FusionContentRise>;
  public selectedVideo?: SpfBucketFileFrontend<string, FusionContentVideo>;
  public selectedLxt?: SpfBucketFileFrontend<string, FusionContentLxt>;
  public selectedMiroByTemplate?: SpfBucketFileFrontend<string, FusionContentMiroByTemplate>;
  public selectedMiroByUrl?: SpfBucketFileFrontend<string, FusionContentMiroByUrl>;
  public selectedIframely?: SpfBucketFileFrontend<string, FusionContentIframely>;
  public selectedYoutube?: SpfBucketFileFrontend<string, FusionContentYoutube>;
  public selectedStoryline?: SpfBucketFileFrontend<string, FusionContentStoryline>;
  public selectedCaptivate?: SpfBucketFileFrontend<string, FusionContentCaptivate>;


  public uploadProgress?: Parameters<AdminServerToClientEvents["fusionUploadProgress"]>["0"];
  public onFusionConfigChanged = new Subject<Parameters<AdminServerToClientEvents["fusionConfigFieldChanged"]>[0]>();
  public onFusionConfigCreated = new Subject<Parameters<AdminServerToClientEvents["fusionConfigCreated"]>[0]>();
  public onFusionConfigDeleted = new Subject<Parameters<AdminServerToClientEvents["fusionConfigDelete"]>[0]>();

  constructor(public httpClient: HttpClient,
    private adminSocketService: SocketAdminService,
    public botIdService: BotidService,
    public loginService: LoginService,
    public toastr: ToastrService,
    private languageService: LanguageService,
    private counterService: CounterService
  ) {
    void this.initSockets();
  }

  public async initSockets() {
    const io = await this.adminSocketService.getIo();
    io.on("fusionConfigDelete", (data) => {
      const { fusionId, botId } = data;
      if (botId != this.botIdService.getBotId()) {
        return;
      }
      const index = this.fusionContent.findIndex(fc => fc._id == fusionId);
      this.fusionContent.splice(index, 1);
      this.onFusionConfigDeleted.next(data);
    });

    io.on("fusionConfigCreated", (result: SpfBucketFileFrontend<string, FusionContent<string>>) => {
      if (result.metadata.botId != this.botIdService.getBotId()) {
        return;
      }
      let insertAt = 0;
      this.fusionContent.map((fc, index) => {
        if (result.metadata.sortFloat > fc.metadata.sortFloat) {
          insertAt = index;
        }
      });
      this.fusionContent.splice(insertAt + 1, 0, result);
      this.onFusionConfigCreated.next(result);
    });

    io.on("fusionConfigFieldChanged", (data) => {
      if (data.botId != this.botIdService.getBotId()) {
        return;
      }
      const index = this.fusionContent.findIndex(fc => fc._id == data.fusionId);
      if (index >= 0 && this.fusionContent[index]) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (this.fusionContent[index].metadata[data.field] as any) = data.value;
        switch (this.fusionContent[index].metadata.type) {
          case 'youtube':
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            if (this.selectedYoutube && this.selectedYoutube._id === data.fusionId) (this.selectedYoutube.metadata[data.field] as any) = data.value;
            break;
          case 'htmlpage':
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            if (this.selectedHtmlPage && this.selectedHtmlPage._id === data.fusionId) (this.selectedHtmlPage.metadata[data.field] as any) = data.value;
            break;
          default:
            break;
        }
      }

      if (data.by != this.loginService.loggedInUser?.name) {
        this.toastr.info("BOT_UPDATED", `durch ${data.by}`);
      }
      this.onFusionConfigChanged.next(data);
    });

    io.on("fusionUploadProgress", (data) => {
      if (data.step == data.totalSteps) {
        this.uploadProgress = undefined;
      } else {
        this.uploadProgress = data;
      }
    });
  }

  public async loadFusionInfo(botId: string) {
    this.fusionContent = await firstValueFrom(this.httpClient.get<SpfBucketFileFrontend<string, FusionContentRise>[]>('/api/toolbox/bot/admin/fusion/list/' + botId));
    this.counterService.setFusionView(this.botIdService.getBotId(), this.fusionContent.length);
  }

  public async loadRiseInfo(botId: string) {
    this.fusionContent = await firstValueFrom(this.httpClient.get<SpfBucketFileFrontend<string, FusionContentRise>[]>('/api/toolbox/bot/admin/fusion/info/rise/' + botId));
  }

  /**
   * Sanitizes fusion view titles by replacing characters that could break JavaScript string literals
   * Fixes AITOOLS-1897: YouTube videos with apostrophes in names break FusionView
   * @param text The text to sanitize
   * @returns Sanitized text safe for use in JavaScript contexts
   */
  private sanitizeFusionText(text: string): string {
    return text
      // Replace single quotes/apostrophes and backticks with space or remove them
      // These characters can break JavaScript string literals when embedded in code
      .replace(/['`]/g, '')
      // Replace double quotes with nothing to avoid breaking JS strings
      .replace(/"/g, '')
      // Remove or replace other potentially problematic characters
      .replace(/[\r\n\t]/g, ' ')
      // Replace multiple spaces with single space
      .replace(/\s+/g, ' ')
      .trim();
  }

  public async updateFusionField(field: string, value: unknown): Promise<FusionFileFrontend> {
    // Sanitize title and name fields to prevent JS injection issues (AITOOLS-1897)
    if ((field === 'title' || field === 'name') && typeof value === 'string') {
      value = this.sanitizeFusionText(value);
    }

    const result = await firstValueFrom(this.httpClient.post<FusionFileFrontend>('/api/toolbox/bot/admin/fusion/updatefusionconfigfield/' + this.botIdService.getBotId() + '/' + this.selectedFusionContent?._id, { field, value }));
    this.toastr.success("SAVED");
    if (field === "sortFloat") {
      this.fusionContent.sort((a, b) => {
        if (a.metadata.sortFloat < b.metadata.sortFloat) {
          return -1;
        }
        if (a.metadata.sortFloat > b.metadata.sortFloat) {
          return 1;
        }
        return 0;
      });
    }
    return result;
  }

  public async updateFusionFieldFor(fusionId: string, field: string, value: unknown): Promise<FusionFileFrontend> {
    if ((field === 'title' || field === 'name') && typeof value === 'string') {
      value = this.sanitizeFusionText(value);
    }

    const result = await firstValueFrom(this.httpClient.post<FusionFileFrontend>('/api/toolbox/bot/admin/fusion/updatefusionconfigfield/' + this.botIdService.getBotId() + '/' + fusionId, { field, value }));
    this.toastr.success("SAVED");
    return result;
  }

  public async createFusionConfig(title: string, type: FusionContent<string>["type"]): Promise<void> {


    // Sanitize title to prevent JS injection issues (AITOOLS-1897)
    const sanitizedTitle = this.sanitizeFusionText(title);

    this.counterService.addFusionView(this.botIdService.getBotId());
    await firstValueFrom(this.httpClient.post<FusionFileFrontend>('/api/toolbox/bot/admin/fusion/createfusionconf/', { botId: this.botIdService.getBotId(), title: sanitizedTitle, type, botlang: this.languageService.getLanguage() }));
    this.toastr.success("SAVED");
    return;
  }

  public async embedInfo(url: string) {
    return await firstValueFrom(this.httpClient.get<{ info: { url: string, html: string }, status: string }>('/api/toolbox/bot/admin/fusion/urlpreview/' + encodeURIComponent(url)));
  }

  public async getHtml(url: string) {
    return await firstValueFrom(this.httpClient.get<{ info: { url: string, html: string }, status: string }>('/api/toolbox/bot/admin/fusion/urlcontent/' + encodeURIComponent(url)));
  }

  public async deleteFile(botId: string, type: string) {
    await firstValueFrom(this.httpClient.post('/api/toolbox/bot/admin/fusion/delete/' + type + '/' + botId, {}));
  }

  public async deleteRiseFile(botId: string) {
    await this.deleteFile(botId, "rise");
  }

  public async delete(botId: string, fusionId: string) {
    this.counterService.removeFusionView(this.botIdService.getBotId());
    await firstValueFrom(this.httpClient.delete('/api/toolbox/bot/admin/fusion/delete/' + botId + '/' + fusionId));
    this.toastr.success("DELETED");
  }

  public async upload(file: File, fusionId: string, botId: string) {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('botId', botId);
    formData.append('fusionId', fusionId);
    try {
      this.uploadProgress = { fusionId, botId, message: "uploading...", step: 1, totalSteps: 10 };
      type ResultType = SpfBucketFileFrontend<string, FusionContentLxt> | SpfBucketFileFrontend<string, FusionContentCaptivate> | SpfBucketFileFrontend<string, FusionContentRise> | SpfBucketFileFrontend<string, FusionContentStoryline>
      const res = await firstValueFrom(this.httpClient.post<ResultType>('/api/toolbox/bot/admin/fusion/upload', formData));
      if (this.selectedRise && res.metadata.type == "rise") {
        this.selectedRise = res as SpfBucketFileFrontend<string, FusionContentRise>;
      }
      if (this.selectedLxt && res.metadata.type == "lxt") {
        this.selectedLxt = res as SpfBucketFileFrontend<string, FusionContentLxt>;
      }
      if (this.selectedStoryline && res.metadata.type == "storyline") {
        this.selectedStoryline = res as SpfBucketFileFrontend<string, FusionContentStoryline>;
      }
      if (this.selectedCaptivate && res.metadata.type == "captivate") {
        this.selectedCaptivate = res as SpfBucketFileFrontend<string, FusionContentCaptivate>;
      }
      return { file, response: res };
    } catch (error) {
      console.error('Error while uploading file', error);
      return { response: 'nok' };
    }
  }
}
